import React, { useState, useEffect } from 'react';
import { UserSignature, StepPattern, GaitTransition } from './interfaces/types';

interface UserIdentificationProps {
  enabled: boolean;
  onUserIdentified: (userId: string, confidence: number) => void;
  knownUsers: UserSignature[];
  minimumConfidenceThreshold?: number;
}

const UserIdentification: React.FC<UserIdentificationProps> = ({
  enabled,
  onUserIdentified,
  knownUsers,
  minimumConfidenceThreshold = 70
}) => {
  const [currentStepPatterns, setCurrentStepPatterns] = useState<StepPattern[]>([]);
  const [identifiedUser, setIdentifiedUser] = useState<{userId: string, confidence: number} | null>(null);
  const [detectedGaitTransition, setDetectedGaitTransition] = useState<GaitTransition | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('Waiting for movement data...');
  
  // Simulate receiving step pattern data
  useEffect(() => {
    if (!enabled) {
      setStatusMessage('User identification disabled');
      return;
    }
    
    setStatusMessage('Monitoring for movement patterns...');
    
    // Simulate sensors collecting step pattern data
    const stepDataInterval = setInterval(() => {
      // Get current time to make simulation time-dependent
      const now = new Date();
      const isMorningTime = now.getHours() >= 6 && now.getHours() <= 9;
      
      // In reality, this data would come from pressure sensors in the floor
      if (isMorningTime && Math.random() > 0.7) {
        // Simulate detecting steps with 30% probability during morning hours
        const simulatedStepPattern: StepPattern = {
          timestamp: now,
          pressureMap: generateSimulatedPressureMap(),
          centerOfPressure: {
            x: 50 + (Math.random() * 10 - 5),
            y: 50 + (Math.random() * 10 - 5)
          },
          weightDistribution: {
            leftRight: (Math.random() * 0.4) - 0.2, // Between -0.2 and 0.2
            heelToe: 0.3 + (Math.random() * 0.4 - 0.2) // Between 0.1 and 0.5 (heel-heavy)
          }
        };
        
        // Add to current step patterns and keep only the most recent 10
        setCurrentStepPatterns(prev => [...prev, simulatedStepPattern].slice(-10));
        setStatusMessage('Movement detected, analyzing pattern...');
      }
    }, 1000);
    
    return () => {
      clearInterval(stepDataInterval);
    };
  }, [enabled]);
  
  // Process step patterns to identify transitions and users
  useEffect(() => {
    if (!enabled || currentStepPatterns.length < 5) return;
    
    // Check if we've collected enough consecutive steps to analyze a transition
    if (isTransitionDetected(currentStepPatterns)) {
      // Extract gait transition features
      const transition = extractGaitTransition(currentStepPatterns);
      setDetectedGaitTransition(transition);
      setStatusMessage('Gait transition detected, identifying user...');
      
      // Compare with known user signatures to identify the user
      const identification = identifyUserFromTransition(transition, knownUsers);
      
      // Update state with identified user
      setIdentifiedUser(identification);
      
      // Notify parent component if confidence is above threshold
      if (identification && identification.confidence >= minimumConfidenceThreshold) {
        onUserIdentified(identification.userId, identification.confidence);
        setStatusMessage(`User identified: ${identification.userId} (${identification.confidence.toFixed(0)}% confidence)`);
      } else if (identification) {
        setStatusMessage(`Possible user: ${identification.userId} (${identification.confidence.toFixed(0)}% confidence, below threshold)`);
      } else {
        setStatusMessage('Unable to identify user with sufficient confidence');
      }
    }
  }, [currentStepPatterns, knownUsers, enabled, onUserIdentified, minimumConfidenceThreshold]);
  
  // Helper function to generate a simulated pressure map
  const generateSimulatedPressureMap = (): number[][] => {
    // Create a 10x10 grid with simulated pressure values
    const map: number[][] = [];
    for (let i = 0; i < 10; i++) {
      const row: number[] = [];
      for (let j = 0; j < 10; j++) {
        // Center of the pressure map has higher values
        const distFromCenter = Math.sqrt(Math.pow((i - 5), 2) + Math.pow((j - 5), 2));
        const pressure = Math.max(0, 100 - (distFromCenter * 20));
        row.push(pressure + (Math.random() * 10 - 5));
      }
      map.push(row);
    }
    return map;
  };
  
  // Helper function to detect a gait transition from stationary to walking
  const isTransitionDetected = (patterns: StepPattern[]): boolean => {
    if (patterns.length < 5) return false;
    
    // Check for increasing movement in the patterns
    const movementMetrics = patterns.map((pattern, index) => {
      if (index === 0) return 0;
      
      // Calculate movement between consecutive patterns
      const prevPattern = patterns[index - 1];
      const xDiff = pattern.centerOfPressure.x - prevPattern.centerOfPressure.x;
      const yDiff = pattern.centerOfPressure.y - prevPattern.centerOfPressure.y;
      
      return Math.sqrt(xDiff * xDiff + yDiff * yDiff);
    });
    
    // Check if the first few are small (stationary) and the later ones are larger (walking)
    const earlyMovement = movementMetrics.slice(0, 2).reduce((sum, val) => sum + val, 0) / 2;
    const lateMovement = movementMetrics.slice(-3).reduce((sum, val) => sum + val, 0) / 3;
    
    // Return true if late movement is significantly larger than early movement
    return lateMovement > earlyMovement * 2;
  };
  
  // Extract gait transition features from step patterns
  const extractGaitTransition = (patterns: StepPattern[]): GaitTransition => {
    // Calculate stride length from consecutive steps
    const strideLengths = [];
    for (let i = 2; i < patterns.length; i++) {
      const p1 = patterns[i-2];
      const p2 = patterns[i];
      
      // Calculate Euclidean distance between centers of pressure
      const xDiff = p2.centerOfPressure.x - p1.centerOfPressure.x;
      const yDiff = p2.centerOfPressure.y - p1.centerOfPressure.y;
      const distance = Math.sqrt(xDiff * xDiff + yDiff * yDiff);
      
      strideLengths.push(distance);
    }
    
    // Calculate average stride length
    const averageStrideLength = strideLengths.reduce((sum, len) => sum + len, 0) / strideLengths.length;
    
    // Calculate step cadence (steps per minute)
    if (patterns.length < 2) return {
      initialSteps: patterns,
      strideLength: 0,
      stepCadence: 0,
      weightDistribution: { leftRight: 0, heelToe: 0 }
    };
    
    const firstTimestamp = patterns[0].timestamp;
    const lastTimestamp = patterns[patterns.length - 1].timestamp;
    const timeSpanMs = lastTimestamp.getTime() - firstTimestamp.getTime();
    const timeSpanMinutes = timeSpanMs / (1000 * 60);
    const stepCadence = patterns.length / timeSpanMinutes;
    
    // Calculate average weight distribution
    const avgWeightDistribution = {
      leftRight: patterns.reduce((sum, p) => sum + p.weightDistribution.leftRight, 0) / patterns.length,
      heelToe: patterns.reduce((sum, p) => sum + p.weightDistribution.heelToe, 0) / patterns.length
    };
    
    return {
      initialSteps: patterns,
      strideLength: averageStrideLength,
      stepCadence,
      weightDistribution: avgWeightDistribution
    };
  };
  
  // Identify user from transition by comparing with known signatures
  const identifyUserFromTransition = (
    transition: GaitTransition, 
    users: UserSignature[]
  ): {userId: string, confidence: number} | null => {
    if (users.length === 0) return null;
    
    // For each user, calculate a similarity score
    const scores = users.map(user => {
      if (user.morningGaitTransitions.length === 0) return { userId: user.userId, score: 0 };
      
      // Compare stride length, cadence, and weight distribution
      const strideLengthScores = user.morningGaitTransitions.map(t => {
        const diff = Math.abs(t.strideLength - transition.strideLength);
        // Normalize: 1.0 is perfect match, 0.0 is poor match
        return Math.max(0, 1 - (diff / 20));
      });
      
      const cadenceScores = user.morningGaitTransitions.map(t => {
        const diff = Math.abs(t.stepCadence - transition.stepCadence);
        return Math.max(0, 1 - (diff / 30));
      });
      
      const weightDistScores = user.morningGaitTransitions.map(t => {
        const lrDiff = Math.abs(t.weightDistribution.leftRight - transition.weightDistribution.leftRight);
        const htDiff = Math.abs(t.weightDistribution.heelToe - transition.weightDistribution.heelToe);
        return Math.max(0, 1 - (lrDiff + htDiff));
      });
      
      // Combine scores from best matching transition
      const bestScoreIndex = findBestScoreIndex(
        strideLengthScores.map((s, i) => s + cadenceScores[i] + weightDistScores[i])
      );
      
      const combinedScore = 
        (strideLengthScores[bestScoreIndex] * 0.3) + 
        (cadenceScores[bestScoreIndex] * 0.3) + 
        (weightDistScores[bestScoreIndex] * 0.4);
      
      return {
        userId: user.userId,
        score: combinedScore
      };
    });
    
    // Find the best score
    const bestScore = Math.max(...scores.map(s => s.score));
    const bestUser = scores.find(s => s.score === bestScore);
    
    // Convert score to confidence percentage
    if (bestUser && bestUser.score > 0.5) {
      return {
        userId: bestUser.userId,
        confidence: bestUser.score * 100
      };
    }
    
    return null;
  };
  
  // Helper function to find the index of the highest score
  const findBestScoreIndex = (scores: number[]): number => {
    return scores.reduce((bestIdx, score, idx, arr) => 
      score > arr[bestIdx] ? idx : bestIdx, 0);
  };
  
  // Render component
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-medium mb-3">User Identification</h3>
      
      <div className="mb-4">
        <div className={`px-3 py-1 rounded-full text-sm font-medium inline-block
          ${!enabled ? 'bg-gray-100 text-gray-800' :
          identifiedUser ? 'bg-green-100 text-green-800' : 
          'bg-blue-100 text-blue-800'}`}>
          {!enabled ? 'Disabled' : 
          identifiedUser ? `User Identified` : 
          'Monitoring'}
        </div>
      </div>
      
      <div className="mb-3 text-sm text-gray-700">
        {statusMessage}
      </div>
      
      {identifiedUser && (
        <div className="mb-4 border-l-4 border-green-500 pl-3 py-2 bg-green-50">
          <div className="font-medium">
            {knownUsers.find(u => u.userId === identifiedUser.userId)?.name || identifiedUser.userId}
          </div>
          <div className="text-sm text-gray-700">
            Confidence: {identifiedUser.confidence.toFixed(1)}%
            {identifiedUser.confidence < minimumConfidenceThreshold && 
              ` (below ${minimumConfidenceThreshold}% threshold)`}
          </div>
        </div>
      )}
      
      {detectedGaitTransition && (
        <div className="text-sm">
          <h4 className="font-medium mb-2">Detected Gait Metrics</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="border rounded p-2">
              <div className="text-xs text-gray-500">Stride Length</div>
              <div className="font-medium">{detectedGaitTransition.strideLength.toFixed(1)} cm</div>
            </div>
            <div className="border rounded p-2">
              <div className="text-xs text-gray-500">Step Cadence</div>
              <div className="font-medium">{detectedGaitTransition.stepCadence.toFixed(1)} steps/min</div>
            </div>
            <div className="border rounded p-2">
              <div className="text-xs text-gray-500">Weight Balance</div>
              <div className="font-medium">
                {detectedGaitTransition.weightDistribution.leftRight >= 0 ? 'Right' : 'Left'} 
                ({Math.abs(detectedGaitTransition.weightDistribution.leftRight * 100).toFixed(0)}%)
              </div>
            </div>
            <div className="border rounded p-2">
              <div className="text-xs text-gray-500">Foot Pressure</div>
              <div className="font-medium">
                {detectedGaitTransition.weightDistribution.heelToe >= 0 ? 'Heel' : 'Toe'} 
                ({Math.abs(detectedGaitTransition.weightDistribution.heelToe * 100).toFixed(0)}%)
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="mt-4">
        <h4 className="text-sm font-medium mb-2">Known Users</h4>
        <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
          {knownUsers.map(user => (
            <div key={user.userId} className={`p-2 border rounded flex justify-between items-center
              ${identifiedUser?.userId === user.userId ? 'bg-green-50 border-green-200' : ''}`}>
              <div>
                <div className="font-medium">{user.name}</div>
                <div className="text-gray-500">
                  Patterns: {user.morningGaitTransitions.length}
                </div>
              </div>
              {identifiedUser?.userId === user.userId && (
                <div className="text-green-600 text-sm font-medium">
                  Match
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserIdentification; 